import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { Breadcrumbs, Tabs, Icon, Badge, Button, Skeleton, EmptyState, SectionHead, PlaceCard } from '../../components/ui';
import { fetchTempleBySlug, fetchAllTemples } from '../../services/templeService';
import { resolveTempleSlugAlias } from '../../routes/templeAliases';

const FALLBACK_IMG = '/images/design/ram-mandir-complex.webp';
const LOCAL_IMAGE = [
  [/janmabhoomi|ram mandir/i, '/images/design/hero-ram-mandir.webp'],
  [/hanuman/i, '/images/hanuman/temple-1.webp'],
  [/kanak/i, '/images/kanak-bhawan/temple.jpg'],
  [/nageshwar/i, '/images/ghats/naya-ghat-1.jpg'],
];
const localImage = (name = '') => (LOCAL_IMAGE.find(([re]) => re.test(name)) || [null, FALLBACK_IMG])[1];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatHours(openingHours) {
  if (!openingHours) return null;
  const groups = [];
  let i = 0;
  while (i < DAYS.length) {
    const val = openingHours[DAYS[i]];
    let j = i + 1;
    while (j < DAYS.length && openingHours[DAYS[j]] === val) j += 1;
    groups.push({ label: j - i > 1 ? `${LABELS[i]}–${LABELS[j - 1]}` : LABELS[i], hours: val });
    i = j;
  }
  return groups;
}

const ETIQUETTE = [
  'Dress modestly — shoulders and knees covered.',
  'Leave phones, cameras and leather items at the counter.',
  'Join the queue calmly; separate lines exist for seniors.',
];

const titleFromSlug = (slug = '') => slug
  .split('-')
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ');

export default function TempleDetails() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();

  const [temple, setTemple] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [related, setRelated] = useState([]);
  const [tab, setTab] = useState('about');
  const [imgIndex, setImgIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    const canonicalSlug = resolveTempleSlugAlias(slug);
    if (canonicalSlug) {
      navigate(`/temples/${canonicalSlug}`, { replace: true });
      return;
    }
    let alive = true;
    window.scrollTo(0, 0);
    setLoading(true); setNotFound(false); setLoadError(false); setTab('about'); setImgIndex(0);
    fetchTempleBySlug(slug)
      .then((data) => {
        if (!alive) return;
        setTemple(data);
        return fetchAllTemples({ limit: 6 });
      })
      .then((res) => {
        if (!alive || !res) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        setRelated(list.filter((x) => x.slug !== slug).slice(0, 3));
      })
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug, navigate]);

  const images = temple?.images?.length ? temple.images : [localImage(temple?.name)];
  const prevImg = useCallback(() => setImgIndex((i) => (i === 0 ? images.length - 1 : i - 1)), [images.length]);
  const nextImg = useCallback(() => setImgIndex((i) => (i === images.length - 1 ? 0 : i + 1)), [images.length]);

  useEffect(() => {
    if (!galleryOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prevImg();
      if (e.key === 'ArrowRight') nextImg();
      if (e.key === 'Escape') setGalleryOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [galleryOpen, prevImg, nextImg]);

  // Crawlers and the prerenderer can land here before the API answers, so this
  // state still needs a title, description and canonical of its own.
  if (loading) {
    const name = titleFromSlug(slug);
    return (
      <>
        <SEO
          title={name}
          description={`${name} in Ayodhya — darshan timings, significance and how to reach.`}
          canonicalUrl={`/temples/${slug}`}
        />
        <div className="container detail-wrap">
          <h1 className="sr-only">{name}</h1>
          <div className="detail">
            <div className="detail__main"><Skeleton height={360} radius="var(--r-xl)" /><Skeleton height={40} width="55%" /><Skeleton height={160} /></div>
            <div className="detail__aside"><Skeleton height={260} radius="var(--r-lg)" /></div>
          </div>
        </div>
      </>
    );
  }

  // The API said this temple does not exist.
  if (notFound) {
    return (
      <div className="container detail-wrap">
        <SEO title={`${titleFromSlug(slug)} — not found`} canonicalUrl={`/temples/${slug}`} noindex />
        <EmptyState icon="temple" title={t('Temple not found')} body={t('This temple may have been renamed. Browse all temples instead.')} action={<Button variant="outline" to="/temples">{t('Back to temples')}</Button>} />
      </div>
    );
  }

  // We could not reach the API. The page is still valid, so keep it indexable
  // and describe it from the slug rather than claiming it does not exist.
  if (!temple) {
    const name = titleFromSlug(slug);
    return (
      <>
        <SEO
          title={name}
          description={`${name} in Ayodhya — darshan timings, significance and how to reach.`}
          canonicalUrl={`/temples/${slug}`}
        />
        <div className="container detail-wrap">
          <Breadcrumbs className="detail__crumbs" items={[{ label: t('Home'), to: '/' }, { label: t('Temples & Darshan'), to: '/temples' }, { label: name }]} />
          <h1 className="detail__title" style={{ marginBottom: 12 }}>{name}</h1>
          <EmptyState
            icon={loadError ? 'circle-alert' : 'temple'}
            title={t('Details are taking a moment')}
            body={t('We could not load this temple just now. Please try again, or browse all temples.')}
            action={<Button variant="outline" onClick={() => window.location.reload()}>{t('Retry')}</Button>}
          />
        </div>
      </>
    );
  }

  const hours = formatHours(temple.openingHours);
  const todayHours = temple.openingHours?.[DAYS[(new Date().getDay() + 6) % 7]] || t('Open daily');
  const canonicalSlug = temple.slug || slug;

  const tabs = [
    { id: 'about', label: t('About'), icon: 'info' },
    temple.history && { id: 'history', label: t('History'), icon: 'landmark' },
    temple.significance && { id: 'significance', label: t('Significance'), icon: 'om' },
    hours && { id: 'timings', label: t('Timings'), icon: 'clock' },
  ].filter(Boolean);

  const templeSchema = {
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    name: temple.name,
    description: temple.description,
    image: images[0],
    url: `https://ayodhyadham.info/temples/${canonicalSlug}`,
    address: { '@type': 'PostalAddress', streetAddress: temple.address, addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', postalCode: '224123', addressCountry: 'IN' },
    openingHoursSpecification: (hours || []).map((g) => ({
      '@type': 'OpeningHoursSpecification', dayOfWeek: g.label,
      opens: g.hours?.split(/\s*[-–]\s*/)[0] || '06:00',
      closes: g.hours?.split(/\s*[-–]\s*/)[1] || '21:00',
    })),
    touristType: 'Hindu pilgrims, religious tourists, history enthusiasts',
    isAccessibleForFree: true,
  };


  return (
    <>
      <SEO
        title={`${temple.name} — Temple in Ayodhya`}
        description={temple.description?.slice(0, 160)}
        canonicalUrl={`/temples/${canonicalSlug}`}
        image={images[0]}
        keywords={`${temple.name}, temple in Ayodhya, darshan timings, ${temple.name} significance, Ayodhya pilgrimage`}
        structuredData={templeSchema}
      />

      <div className="container detail-wrap">
        <Breadcrumbs className="detail__crumbs" items={[{ label: t('Home'), to: '/' }, { label: t('Temples & Darshan'), to: '/temples' }, { label: temple.name }]} />

        <div className="detail">
          <div className="detail__main">
            <div className="gallery">
              <button type="button" className="gallery__main" onClick={() => setGalleryOpen(true)} aria-label={t('Open gallery')} style={{ border: 0, padding: 0 }}>
                <img src={images[imgIndex]} alt={temple.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
                {images.length > 1 && <Badge variant="soft" className="gallery__count"><Icon name="camera" size={13} />{imgIndex + 1}/{images.length}</Badge>}
              </button>
              {images.length > 1 && (
                <div className="gallery__thumbs">
                  {images.map((src, i) => (
                    <button key={src} type="button" className={`gallery__thumb${i === imgIndex ? ' is-active' : ''}`} onClick={() => setImgIndex(i)} aria-label={t('View image {{n}}', { n: i + 1 })}>
                      <img src={src} alt="" loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="detail__head">
              <div className="detail__tags">
                <Badge variant="soft">{t('Temple')}</Badge>
                {temple.featured && <Badge variant="gold">{t('Featured')}</Badge>}
                {temple.popularity >= 8 && <Badge variant="outline">{t('Most visited')}</Badge>}
              </div>
              <h1 className="detail__title">{temple.name}</h1>
              <div className="detail__meta">
                <span><Icon name="clock" size={15} />{todayHours}</span>
                {temple.address && <span><Icon name="map-pin" size={15} />{temple.address}</span>}
                <span><Icon name="indian-rupee" size={15} />{t('Free entry')}</span>
              </div>
            </div>

            <section className="detail__section">
              <Tabs variant="underline" items={tabs} value={tab} onChange={setTab} ariaLabel={t('Temple information')} />
              <div style={{ marginTop: 18 }}>
                {tab === 'about' && <div className="prose"><p>{temple.description}</p></div>}
                {tab === 'history' && <div className="prose"><p>{temple.history}</p></div>}
                {tab === 'significance' && <div className="prose"><p>{temple.significance}</p></div>}
                {tab === 'timings' && (
                  <ul className="info-list">
                    {hours.map((g) => (
                      <li key={g.label}>
                        <span className="info-list__icon"><Icon name="clock" size={16} /></span>
                        <div><div className="info-list__label">{g.label}</div><div className="info-list__value">{g.hours || t('Closed')}</div></div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {related.length > 0 && (
              <section className="detail__section related">
                <SectionHead title={t('Nearby temples')} as="h2" action={{ label: t('View All'), to: '/temples' }} />
                <div className="card-grid">
                  {related.map((r) => (
                    <PlaceCard
                      key={r.id}
                      image={r.images?.[0] || localImage(r.name)}
                      title={r.name}
                      subtitle={(r.significance || r.description || '').slice(0, 90)}
                      to={`/temples/${r.slug}`}
                      meta={[{ icon: 'clock', text: r.openingHours?.monday || t('Open daily') }]}
                      cta={t('Explore')}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="detail__aside">
            <div className="card aside-card">
              <h3 className="h-card">{t('Plan your darshan')}</h3>
              <div className="aside-card__rows">
                <div className="aside-card__row"><span>{t("Today's hours")}</span><span>{todayHours}</span></div>
                <div className="aside-card__row"><span>{t('Entry')}</span><span>{t('Free')}</span></div>
                {temple.location && <div className="aside-card__row"><span>{t('Location')}</span><span>{temple.location}</span></div>}
              </div>
              <Button block to={`/darshan-booking?temple=${canonicalSlug}`} icon={<Icon name="arrow-right" size={16} />}>{t('Book Darshan')}</Button>
              <Button block variant="outline" href={`https://maps.google.com/?q=${encodeURIComponent(`${temple.name}, Ayodhya`)}`} icon={<Icon name="navigation" size={16} />} iconPosition="left">{t('Get Directions')}</Button>
            </div>

            <div className="card aside-card" style={{ background: 'var(--surface-2)' }}>
              <h3 className="h-card">{t('Temple etiquette')}</h3>
              <ul className="check-list">
                {ETIQUETTE.map((e) => <li key={e}><Icon name="circle-check" size={16} />{t(e)}</li>)}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {galleryOpen && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={t('Gallery')} onClick={() => setGalleryOpen(false)}>
          <img src={images[imgIndex]} alt={temple.name} onClick={(e) => e.stopPropagation()} />
          <button type="button" className="lightbox__close" aria-label={t('Close')} onClick={() => setGalleryOpen(false)}><Icon name="x" size={20} /></button>
          {images.length > 1 && (
            <>
              <button type="button" className="lightbox__prev" aria-label={t('Previous')} onClick={(e) => { e.stopPropagation(); prevImg(); }}><Icon name="chevron-left" size={20} /></button>
              <button type="button" className="lightbox__next" aria-label={t('Next')} onClick={(e) => { e.stopPropagation(); nextImg(); }}><Icon name="chevron-right" size={20} /></button>
              <div className="lightbox__counter">{imgIndex + 1} / {images.length}</div>
            </>
          )}
        </div>
      )}
    </>
  );
}
